import React, { useEffect } from 'react';
import t from 'utils/translate';
import { useSelector } from 'react-redux';
import { selectCompanySettings, selectDataLoaded } from '../../settings/stores/company-settings-slice-handler';
import Tooltip from '@mui/material/Tooltip';
import Preloader from '../../shared/components/preloader';
import {
    selectContactsPageData,
    triggerFirstAction,
    getContactsSelectorData,
    updateContactsPageData,
    selectFavoriteContactsData,
    updateContactsSelectorData,
    registerContactsSelector,
    openPbxDialer,
} from '../stores/contacts-slice-handler';
import ContactsSelector from './contacts-selector';
import {
    ContactMenuOption,
    ContactMenuType,
    CONTACTS_PAGE_FIXED_SELECTOR_ID,
} from '../../../constants/contacts-constants';
import ContactMenu from './contact-menu';
import { selectSettingsJson } from '../../settings/stores/user-settings-slice-handler';
import Log from 'utils/log';
import classNames from 'classnames';
import ContactsFilter from './contacts-filter';
import { openGuestInvitationModal, selectGuestInvitationPopupOn } from '../stores/guest-invitation-slice-handler';
import GuestInvitation from './guest-invitation';
import { TabTypes } from '../../../constants/user-groups-constants';

export default function ContactsPageSideBar() {
    // there is a single contacts elector which servers the contacts page
    // in this case the selector can have a fixed id; is helps in debugging
    const contactsSelectorUuid = CONTACTS_PAGE_FIXED_SELECTOR_ID;

    useEffect(() => {
        registerContactsSelector(contactsSelectorUuid);
        updateContactsPageData({ contactsSelectorUuid });
    }, []);

    /**
     * @type {CompanySettings}
     */
    const companySettings = useSelector(selectCompanySettings);
    /**
     * @type {UserSettingsJson}
     */
    const userSettings = useSelector(selectSettingsJson);

    /**
     * @type {ContactsPageData}
     */
    const contactsPageData = useSelector(selectContactsPageData);
    /**
     * @type {ContactsSelectorData}
     */
    const contactsSelectorData = useSelector(() => getContactsSelectorData(contactsSelectorUuid));
    const favoriteContactsData = useSelector(selectFavoriteContactsData);
    const guestInvitationPopupIsOn = useSelector(selectGuestInvitationPopupOn);

    const companySettingsLoaded = useSelector(selectDataLoaded);
    const contactsLoaded = contactsSelectorData && !contactsSelectorData.updateInProgress;
    const dataLoaded = companySettingsLoaded && contactsLoaded;

    const launchCallOut = () => {
        openPbxDialer();
    };

    const openGuestDialog = () => {
        if (guestInvitationPopupIsOn) {
            return;
        }

        openGuestInvitationModal();
    };

    const openFavoritesDialog = () => {
        if (contactsPageData.globalAction === ContactMenuOption.ADD_TO_FAVORITES) {
            return;
        }

        updateContactsPageData({
            globalAction: ContactMenuOption.ADD_TO_FAVORITES,
        });

        updateContactsSelectorData(contactsSelectorUuid, favoriteContactsData);
    };

    /**
     * @param {CugContact} contact
     */
    const onContactNameClick = (contact) => {
        /**
         * @type {ContactMenuOption|null}
         */
        let menuOption = null;

        switch (userSettings.defaultAction) {
            case 'message':
                menuOption = ContactMenuOption.MESSAGE;
                break;

            case 'call':
                if (contact.IsGroup) {
                    Log.notice(
                        'CONTACTS SIDEBAR',
                        'First contact action is set as VoIP call, but this not applies to a group. Will fallback to message action',
                    );
                    menuOption = ContactMenuOption.MESSAGE;
                } else {
                    menuOption = ContactMenuOption.VOIP_CALL;
                }

                break;

            case 'ptt':
                menuOption = ContactMenuOption.PTT_CALL;
                break;
        }

        Log.debug(
            'CONTACTS SIDEBAR',
            `Launching first contact action ${menuOption} for contact ${contact.ContactId} as result of users setting wich has the value ${userSettings.defaultAction}`,
        );

        triggerFirstAction(contact, menuOption, ContactMenuType.CONTACTS_PAGE);
    };

    const renderHeader = () => (
        <>
            <div className="title-bar">
                <div className="page-title">{t('js_all_contact_title')}</div>
                {dataLoaded && (
                    <div className="buttons-container">
                        <Tooltip title={t('js_all_contacts_group_conversation')}>
                            <span
                                data-test-id="sidebar-item-GROUP_CONVERSATION"
                                className={classNames('stw-create-group-conversation', {
                                    'wcf-ui-disabled-control':
                                        contactsPageData.globalAction === ContactMenuOption.GROUP_CONVERSATION,
                                })}
                                onClick={() =>
                                    updateContactsPageData({ globalAction: ContactMenuOption.GROUP_CONVERSATION })
                                }
                            ></span>
                        </Tooltip>

                        {companySettings.AllowWalkieTalkie && (
                            <Tooltip title={t('js_all_option_walkie_talkie')}>
                                <span
                                    data-test-id="sidebar-item-PTT_CALL"
                                    className={classNames('stw-call-ptt', {
                                        'wcf-ui-disabled-control':
                                            contactsPageData.globalAction === ContactMenuOption.PTT_CALL,
                                    })}
                                    onClick={() => updateContactsPageData({ globalAction: ContactMenuOption.PTT_CALL })}
                                ></span>
                            </Tooltip>
                        )}
                        {companySettings.AllowAudioConference && (
                            <Tooltip title={t('js_all_conference_call')}>
                                <span
                                    data-test-id="sidebar-item-CONFERENCE_CALL"
                                    className={classNames('stw-conference-call', {
                                        'wcf-ui-disabled-control':
                                            contactsPageData.globalAction === ContactMenuOption.CONFERENCE_CALL,
                                    })}
                                    onClick={() =>
                                        updateContactsPageData({ globalAction: ContactMenuOption.CONFERENCE_CALL })
                                    }
                                ></span>
                            </Tooltip>
                        )}
                        <div className="buttons-border"></div>
                        <Tooltip title={t('mybusiness_resources_favorites_title')}>
                            <span
                                data-test-id="favorites-button"
                                className={classNames('stw-favorite-active', {
                                    'wcf-ui-disabled-control':
                                        contactsPageData.globalAction === ContactMenuOption.ADD_TO_FAVORITES,
                                })}
                                onClick={() => openFavoritesDialog()}
                            ></span>
                        </Tooltip>
                        <ContactsFilter
                            buttonDisabled={
                                contactsSelectorData && contactsSelectorData.currentTab === TabTypes.GROUPS_TAB
                            }
                        />
                    </div>
                )}
            </div>
        </>
    );

    const additionalControls = () => {
        return (
            <div className="additional-controls">
                <div className="global-menu-container" data-test-id="global-menu-contacts">
                    <ContactMenu
                        menuType={ContactMenuType.CONTACTS_PAGE}
                        optionClick={(contact, menuAction) => {
                            updateContactsPageData({
                                globalAction: menuAction,
                            });
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderContactsSelector = () => {
        if (!dataLoaded || !contactsSelectorData) {
            return <Preloader />;
        }

        return (
            <div className="contacts-selector-container">
                <ContactsSelector
                    contactsSelectorUuid={contactsSelectorUuid}
                    options={{
                        unregisterWhenRemoved: false,
                        applyUsersListFilters: true,
                        multipleSelectionOpen: contactsPageData.globalAction !== null,
                        menuType: ContactMenuType.CONTACTS_PAGE,
                        withFavoriteControls: true,
                        /**
                         * @param {CugContact} contact
                         */
                        onContactNameClick: (contact) => onContactNameClick(contact),
                        users: {
                            renderingOptions: {
                                withStatusDetails: true,
                            },
                            selectedMsisdns: contactsSelectorData.msisdns,
                            restrictions: {
                                onlyHumans: false,
                            },
                        },
                        groups: {
                            selectedGroupsIds: contactsSelectorData.groupsIds,
                        },
                    }}
                    additionalContent={additionalControls()}
                ></ContactsSelector>
            </div>
        );
    };

    return (
        <div className="contacts-page-side-bar wcf-ui-side-bar">
            {contactsPageData.dataIsUpdating && <Preloader asOverlay />}
            {renderHeader()}
            {renderContactsSelector()}
            {companySettings.AllowVirality && <GuestInvitation />}
        </div>
    );
}
